import React, { useState } from "react";
import { motion } from "framer-motion";

export default function OhmsLawExperiment({ onComplete }) {
  const [voltage, setVoltage] = useState(3);
  const [resistance, setResistance] = useState(5);

  const current = +(voltage / resistance).toFixed(2);
  const brightness = Math.min(1, current / 2);

  return (
    <div className="flex flex-col items-center mt-10 px-4">
      <h2 className="text-3xl font-bold text-red-700 mb-6">
        Verification of Ohm’s Law
      </h2>

      {/* CIRCUIT AREA */}
      <div
        className="relative bg-white border rounded shadow"
        style={{
          width: "100%",
          maxWidth: "850px",
          height: "350px",
        }}
      >
        {/* LABELS */}
        <div className="absolute left-[40px] top-[40px] text-xs font-semibold text-gray-600">
          Battery
        </div>
        <div className="absolute left-[220px] top-[40px] text-xs font-semibold text-gray-600">
          Resistor
        </div>
        <div className="absolute left-[390px] top-[40px] text-xs font-semibold text-gray-600">
          Bulb
        </div>
        <div className="absolute left-[540px] top-[40px] text-xs font-semibold text-gray-600">
          Ammeter
        </div>

        {/* WIRES */}
        <svg className="absolute inset-0 w-full h-full">
          {/* Left wire */}
          <line
            x1="80"
            y1="180"
            x2="220"
            y2="180"
            stroke={`rgba(255,0,0,${brightness})`}
            strokeWidth="5"
          />
          {/* Middle wire */}
          <line
            x1="220"
            y1="180"
            x2="350"
            y2="180"
            stroke="black"
            strokeWidth="5"
          />
          {/* Right wire */}
          <line
            x1="350"
            y1="180"
            x2="520"
            y2="180"
            stroke={`rgba(255,0,0,${brightness})`}
            strokeWidth="5"
          />
          {/* Closing wire */}
          <line
            x1="520"
            y1="180"
            x2="80"
            y2="180"
            stroke="black"
            strokeWidth="5"
          />
        </svg>

        {/* CURRENT DIRECTION LABEL */}
        <div className="absolute left-[200px] top-[210px] text-sm text-blue-700 font-semibold">
          ➜ Current Flow (I)
        </div>

        {/* BATTERY */}
        <div className="absolute left-[60px] top-[150px]">
          <Battery voltage={voltage} />
        </div>

        {/* RESISTOR */}
        <div className="absolute left-[200px] top-[165px]">
          <Resistor />
        </div>

        {/* BULB */}
        <div className="absolute left-[345px] top-[130px]">
          <Bulb brightness={brightness} />
        </div>

        {/* AMMETER */}
        <div className="absolute left-[500px] top-[140px]">
          <Ammeter current={current} />
        </div>
      </div>

      {/* CONTROLS */}
      <div className="mt-10 w-full max-w-[600px] p-5 bg-gray-50 rounded shadow">
        {/* VOLTAGE */}
        <div className="mb-6">
          <label className="font-semibold text-lg">
            Voltage (V): {voltage} V
          </label>
          <input
            type="range"
            min={1}
            max={12}
            value={voltage}
            onChange={(e) => {
              setVoltage(+e.target.value);
              onComplete && onComplete();
            }}
            className="w-full"
          />
        </div>

        {/* RESISTANCE */}
        <div className="mb-6">
          <label className="font-semibold text-lg">
            Resistance (R): {resistance} Ω
          </label>
          <input
            type="range"
            min={1}
            max={20}
            value={resistance}
            onChange={(e) => setResistance(+e.target.value)}
            className="w-full"
          />
        </div>

        {/* CURRENT DISPLAY */}
        <div className="text-xl font-bold text-blue-700 text-center mt-4">
          Current (I) = {current} A
        </div>
      </div>
    </div>
  );
}

/* --------------------- COMPONENTS --------------------- */

function Battery({ voltage }) {
  return (
    <div className="flex items-center">
      <div className="w-2 h-12 bg-gray-900"></div>
      <div className="w-4 h-16 bg-gray-500 border-2 border-gray-700"></div>
      <span className="ml-2 text-sm font-bold text-white bg-black">
        +{voltage}V
      </span>
    </div>
  );
}

function Resistor() {
  return (
    <svg width="100" height="40">
      <polyline
        // points="0,20 10,10 20,30 30,10 40,30 50,10 60,20 100,20"
        points="0,20 10,5 20,26 30,2 40,24 50,2 60,18 100,18"
        stroke="orange"
        strokeWidth="4"
        fill="none"
      />
    </svg>
  );
}

function Bulb({ brightness }) {
  return (
    <div className="relative">
      {brightness > 0.05 && (
        <motion.div
          className="absolute w-24 h-24 rounded-full bg-yellow-300 opacity-40"
          style={{
            scale: 1 + brightness * 2,
          }}
        />
      )}
      <div className="w-14 h-14 rounded-full bg-yellow-200 border-4 border-yellow-600 shadow-inner"></div>
    </div>
  );
}

function Ammeter({ current }) {
  return (
    <div className="w-24 h-24 rounded-full border-4 border-black flex items-center justify-center bg-white shadow">
      <div className="text-center">
        <div className="font-bold text-sm">A</div>
        <div className="text-xl font-semibold">{current}</div>
      </div>
    </div>
  );
}
